import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CallHistoryItem } from '../types';

interface CallDetailDrawerOptions {
  contactVariables?: Record<string, string>;
  initialCall?: CallHistoryItem | null;
  initialTab?: 'information' | 'general' | 'variables' | 'dataCollected' | 'scripts' | 'diagnostics';
  controlMode?: 'default' | 'incoming' | 'outbound_self';
}

interface CallDetailDrawerContextValue {
  activeCallId: string | null;
  isOpen: boolean;
  activeOptions: CallDetailDrawerOptions | null;
  openCallDetail: (callId: string, options?: CallDetailDrawerOptions) => void;
  closeCallDetail: () => void;
}

const CallDetailDrawerContext = createContext<CallDetailDrawerContextValue | undefined>(undefined);

/**
 * Global controller for opening and closing the call detail drawer from any protected route.
 * The drawer open state is URL-driven via `?call=<id>`.
 */
export function CallDetailDrawerProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [optionsByCallId, setOptionsByCallId] = useState<Record<string, CallDetailDrawerOptions>>({});

  const activeCallId = searchParams.get('call');

  const openCallDetail = useCallback(
    (callId: string, options?: CallDetailDrawerOptions) => {
      if (!callId) return;
      if (options) {
        setOptionsByCallId((prev) => ({ ...prev, [callId]: options }));
      }
      setSearchParams((prevParams) => {
        const nextParams = new URLSearchParams(prevParams);
        nextParams.set('call', callId);
        return nextParams;
      });
    },
    [setSearchParams],
  );

  const closeCallDetail = useCallback(() => {
    setSearchParams((prevParams) => {
      const nextParams = new URLSearchParams(prevParams);
      nextParams.delete('call');
      return nextParams;
    }, { replace: true });
  }, [setSearchParams]);

  const activeOptions = activeCallId ? optionsByCallId[activeCallId] ?? null : null;

  const value = useMemo<CallDetailDrawerContextValue>(
    () => ({
      activeCallId,
      isOpen: Boolean(activeCallId),
      activeOptions,
      openCallDetail,
      closeCallDetail,
    }),
    [activeCallId, activeOptions, closeCallDetail, openCallDetail],
  );

  return (
    <CallDetailDrawerContext.Provider value={value}>
      {children}
    </CallDetailDrawerContext.Provider>
  );
}

/**
 * Access the global call-detail drawer controller.
 */
export function useCallDetailDrawer() {
  const context = useContext(CallDetailDrawerContext);
  if (!context) {
    throw new Error('useCallDetailDrawer must be used within a CallDetailDrawerProvider');
  }
  return context;
}
